import { useQuery } from '@tanstack/react-query';
import { getGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { PublicSignerType } from '@dealstack/shared/lib/publicSignerType';
import type { SignerSessionInfoRes } from '@dealstack/shared/lib/generated';
import { DocumentSigningSecurityLevel, KYCFileType } from '@dealstack/shared/lib/generated';

export interface WitnessDetails {
	fullName: string;
	address: string;
	occupation: string;
}

/** DC-825: set for a KYC certifier only. */
export interface PublicCertificationInfo {
	kycFileType: KYCFileType;
	investorFullName: string;
	certifierName: string;
	certificationText: string;
	defaultCertificationText: string;
	textEditedAt: string | null;
}

export interface PublicSigningInfo {
	documentName: string;
	signerType: PublicSignerType;
	signatureStatus: string;
	canSign: boolean;
	fileId: string | null;
	effectiveSecurityLevel: DocumentSigningSecurityLevel;
	requiresWitnessDetails: boolean;
	witnessDetails: WitnessDetails | null;
	requiresWitnessNomination: boolean;
	signerName: string | null;
	signingMethod: string | null;
	verificationLevel: string | null;
	signedAt: string | null;
	session: SignerSessionInfoRes;
	certification: PublicCertificationInfo | null;
}

interface PublicSigningInfoResponse {
	payload: PublicSigningInfo;
}

async function fetchPublicSigningInfo(token: string): Promise<PublicSigningInfo> {
	const config = getGlobalConfig();
	const url = `${config.apiUrl}/public-signing/info?token=${encodeURIComponent(token)}`;

	const response = await fetch(url);

	if (!response.ok) {
		const body = await response.json().catch(() => ({}));
		const message = body?.message ?? '';
		if (message.includes('expired')) {
			throw new Error('LINK_EXPIRED');
		}
		throw new Error('FETCH_FAILED');
	}

	const data: PublicSigningInfoResponse = await response.json();
	return data.payload;
}

/**
 * `enabled` lets the caller keep this disabled until it's known the token is
 * signature-scoped (see `PublicSigningPage`'s programme-vs-signature token
 * probe) — this endpoint 401s for a programme-scoped token, so firing it
 * before that's known would surface a spurious error.
 */
export const usePublicSigningInfo = (token: string, enabled = true) => {
	return useQuery({
		queryKey: ['public-signing-info', token],
		queryFn: () => fetchPublicSigningInfo(token),
		enabled: !!token && enabled,
		retry: false,
		staleTime: Infinity,
	});
};
